import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Button, Divider, Select, Spin, Table, Tag, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import api from '../api';
import { STICKY_OFFSET } from '../utils/tableLayout';
import { UserContext } from '../App';

const { Title } = Typography;
const { Option } = Select;

const PRIORITY = ['Break', 'Lunch', 'Unavailable', 'Refused Contact', 'Technical Issue'];

function currentMonthKey() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function generateMonths() {
  const months = [];
  const now = new Date();
  for (let i = -24; i <= 0; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    months.push(`${y}-${m}`);
  }
  return months.sort().reverse();
}

function formatMonthKey(mk) {
  if (!mk) return mk;
  const [y, m] = mk.split('-');
  const labels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${labels[parseInt(m, 10) - 1]} ${y}`;
}

function fmtHrs(mins) {
  if (mins == null || mins === 0) return '—';
  return `${(mins / 60).toFixed(1)}h`;
}

const MONTHS = generateMonths();

function fmtHour(h) {
  if (h === 0)  return '12am';
  if (h < 12)   return `${h}am`;
  if (h === 12) return '12pm';
  return `${h - 12}pm`;
}

export default function TeamCCaaSStatsPage() {
  const currentUser = useContext(UserContext);

  const [teams,           setTeams]           = useState([]);
  const [selectedTeamIds, setSelectedTeamIds] = useState([]);
  const [fromMonth,       setFromMonth]       = useState(currentMonthKey);
  const [toMonth,         setToMonth]         = useState(currentMonthKey);
  const [data,            setData]            = useState([]);
  const [hourlyData,      setHourlyData]      = useState([]);
  const [loading,         setLoading]         = useState(false);
  const [searched,        setSearched]        = useState(false);

  useEffect(() => {
    api.get('/api/teams', { params: { my_teams: 1, no_client_users: 1 } }).then(r => {
      const t = r.data || [];
      setTeams(t);
      const myTeamId = currentUser?.TeamID;
      if (myTeamId && t.some(team => team.ID === myTeamId)) {
        setSelectedTeamIds([myTeamId]);
      }
    });
  }, [currentUser]);

  const handleSearch = () => {
    if (!selectedTeamIds.length) return;
    setLoading(true);
    setSearched(true);
    const params = { team_ids: selectedTeamIds.join(','), from_month: fromMonth, to_month: toMonth };
    Promise.all([
      api.get('/api/team-ccaas-stats',         { params }),
      api.get('/api/team-ccaas-stats/hourly',  { params }),
    ])
      .then(([r1, r2]) => { setData(r1.data || []); setHourlyData(r2.data || []); })
      .finally(() => setLoading(false));
  };

  // ── Derive ordered state list ────────────────────────────────────────────
  const ccaasStates = useMemo(() => {
    const all = [...new Set(data.map(r => r.State))];
    return [
      ...PRIORITY.filter(s => all.includes(s)),
      ...all.filter(s => !PRIORITY.includes(s)).sort(),
    ];
  }, [data]);

  // ── Table view: aggregate across months ─────────────────────────────────
  const tableRows = useMemo(() => {
    const teamMap = {};   // TeamID -> { ...states }
    const callSeen = {}; // TeamID|MonthKey -> TotalContacts (deduplicate per month)

    data.forEach(r => {
      if (!teamMap[r.TeamID]) {
        teamMap[r.TeamID] = { TeamID: r.TeamID, TeamName: r.TeamName, _totalContacts: 0 };
      }
      const t = teamMap[r.TeamID];
      if (!t[r.State]) t[r.State] = { TotalMins: 0, OccurrenceCount: 0 };
      t[r.State].TotalMins       += r.TotalMins;
      t[r.State].OccurrenceCount += r.OccurrenceCount;

      const ck = `${r.TeamID}|${r.MonthKey}`;
      if (!callSeen[ck]) {
        callSeen[ck] = true;
        t._totalContacts += r.TotalContacts;
      }
    });

    const rows = Object.values(teamMap).sort((a, b) => a.TeamName.localeCompare(b.TeamName));
    rows.forEach(row => {
      row._totalMins = ccaasStates.reduce((s, st) => s + (row[st]?.TotalMins ?? 0), 0);
    });
    return rows;
  }, [data, ccaasStates]);

  // ── Hour-over-Hour pivot: rows=states, columns=hours 0-23 ───────────────
  const { hourPivotRows, hourTotals, hourCalls } = useMemo(() => {
    const stateHour  = {};
    const callSeen   = {};
    const callByHour = {};

    hourlyData.forEach(r => {
      if (!stateHour[r.State]) stateHour[r.State] = {};
      stateHour[r.State][r.HourOfDay] = (stateHour[r.State][r.HourOfDay] || 0) + r.TotalMins;

      const ck = `${r.TeamID}|${r.HourOfDay}`;
      if (!callSeen[ck]) {
        callSeen[ck] = true;
        callByHour[r.HourOfDay] = (callByHour[r.HourOfDay] || 0) + r.TotalContacts;
      }
    });

    const hourTotals = {};
    for (let h = 0; h < 24; h++) {
      hourTotals[h] = ccaasStates.reduce((s, st) => s + (stateHour[st]?.[h] || 0), 0);
    }

    const hourPivotRows = ccaasStates.map(state => {
      const row = { _key: state, _label: state };
      for (let h = 0; h < 24; h++) row[h] = stateHour[state]?.[h] ?? null;
      return row;
    });

    return { hourPivotRows, hourTotals, hourCalls: callByHour };
  }, [hourlyData, ccaasStates]);

  const hourPivotRowsWithCalls = useMemo(() => {
    if (!hourPivotRows.length) return hourPivotRows;
    const callRow = { _key: '__calls__', _label: 'Total Calls' };
    for (let h = 0; h < 24; h++) callRow[h] = hourCalls[h] ?? null;
    return [...hourPivotRows, callRow];
  }, [hourPivotRows, hourCalls]);

  const HOURS = Array.from({ length: 24 }, (_, i) => i);

  const hourPivotColumns = [
    { title: 'State', dataIndex: '_label', key: '_state', fixed: 'left', width: 175 },
    ...HOURS.map(h => ({
      title: fmtHour(h),
      key: `h_${h}`,
      width: 90,
      align: 'right',
      render: (_, row) => {
        if (row._key === '__calls__') {
          const v = row[h];
          return v ? <span style={{ fontWeight: 600 }}>{v.toLocaleString()}</span>
                   : <span style={{ color: '#d9d9d9' }}>—</span>;
        }
        const mins = row[h];
        if (!mins) return <span style={{ color: '#d9d9d9' }}>—</span>;
        return (
          <div style={{ textAlign: 'right' }}>
            <Tag style={{ margin: 0 }}>{fmtHrs(mins)}</Tag>
            {pctLabel(mins, hourTotals[h])}
          </div>
        );
      },
    })),
  ];

  // ── Pivot view: rows=states, columns=months ──────────────────────────────
  const { pivotMonths, pivotRows, monthTotals, monthCalls } = useMemo(() => {
    const months = [...new Set(data.map(r => r.MonthKey))].sort(); // ascending

    // state -> monthKey -> TotalMins (summed across teams)
    const stateMonth = {};
    // teamId|monthKey -> TotalContacts (deduplicated)
    const callSeen = {};
    const callByMonth = {}; // monthKey -> TotalContacts

    data.forEach(r => {
      if (!stateMonth[r.State]) stateMonth[r.State] = {};
      stateMonth[r.State][r.MonthKey] = (stateMonth[r.State][r.MonthKey] || 0) + r.TotalMins;

      const ck = `${r.TeamID}|${r.MonthKey}`;
      if (!callSeen[ck]) {
        callSeen[ck] = true;
        callByMonth[r.MonthKey] = (callByMonth[r.MonthKey] || 0) + r.TotalContacts;
      }
    });

    // Sum of all state mins per month
    const monthTotals = {};
    months.forEach(m => {
      monthTotals[m] = ccaasStates.reduce((s, st) => s + (stateMonth[st]?.[m] || 0), 0);
    });

    // Build one row per state
    const pivotRows = ccaasStates.map(state => {
      const row = { _key: state, _label: state };
      months.forEach(m => { row[m] = stateMonth[state]?.[m] ?? null; });
      return row;
    });

    return { pivotMonths: months, pivotRows, monthTotals, monthCalls: callByMonth };
  }, [data, ccaasStates]);

  // ── Column helpers ────────────────────────────────────────────────────────
  const pctLabel = (mins, total) => {
    if (!total || !mins) return null;
    return (
      <div style={{ fontSize: 11, color: 'var(--ant-color-text-tertiary)', lineHeight: 1.2 }}>
        {Math.round((mins / total) * 100)}%
      </div>
    );
  };

  const tableColumns = [
    {
      title: 'Team', dataIndex: 'TeamName', key: 'team', width: 200, fixed: 'left',
      sorter: (a, b) => a.TeamName.localeCompare(b.TeamName),
    },
    {
      title: 'Total Calls', key: '_calls', width: 120, align: 'right',
      sorter: (a, b) => (a._totalContacts ?? 0) - (b._totalContacts ?? 0),
      render: (_, row) => row._totalContacts
        ? <span>{row._totalContacts.toLocaleString()}</span>
        : <span style={{ color: '#d9d9d9' }}>—</span>,
    },
    {
      title: 'Total', key: '_total', width: 110, align: 'right',
      sorter: (a, b) => (a._totalMins ?? 0) - (b._totalMins ?? 0),
      render: (_, row) => row._totalMins
        ? <Tag style={{ margin: 0 }}>{fmtHrs(row._totalMins)}</Tag>
        : <span style={{ color: '#d9d9d9' }}>—</span>,
    },
    ...ccaasStates.map(state => ({
      title: state, key: state, width: 110, align: 'right',
      sorter: (a, b) => (a[state]?.TotalMins ?? -1) - (b[state]?.TotalMins ?? -1),
      render: (_, row) => {
        const cell = row[state];
        if (!cell) return <span style={{ color: '#d9d9d9' }}>—</span>;
        return (
          <div style={{ textAlign: 'right' }}>
            <Tag style={{ margin: 0 }}>{fmtHrs(cell.TotalMins)}</Tag>
            {pctLabel(cell.TotalMins, row._totalMins)}
          </div>
        );
      },
    })),
  ];

  const pivotColumns = [
    {
      title: 'State', dataIndex: '_label', key: '_state', fixed: 'left', width: 175,
    },
    ...pivotMonths.map(m => ({
      title: formatMonthKey(m),
      key: m,
      width: 130,
      align: 'right',
      render: (_, row) => {
        const mins = row[m];
        if (!mins) return <span style={{ color: '#d9d9d9' }}>—</span>;
        return (
          <div style={{ textAlign: 'right' }}>
            <Tag style={{ margin: 0 }}>{fmtHrs(mins)}</Tag>
            {pctLabel(mins, monthTotals[m])}
          </div>
        );
      },
    })),
  ];

  // Append a "Total Calls" summary row to the pivot
  const pivotRowsWithCalls = useMemo(() => {
    if (!pivotRows.length) return pivotRows;
    const callRow = { _key: '__calls__', _label: 'Total Calls' };
    pivotMonths.forEach(m => { callRow[m] = monthCalls[m] ?? null; });
    return [...pivotRows, callRow];
  }, [pivotRows, pivotMonths, monthCalls]);

  const pivotColumnsForCalls = pivotColumns.map(col => {
    if (col.key === '_state') return col;
    return {
      ...col,
      render: (_, row) => {
        if (row._key === '__calls__') {
          const v = row[col.key];
          return v ? <span style={{ fontWeight: 600 }}>{v.toLocaleString()}</span>
                   : <span style={{ color: '#d9d9d9' }}>—</span>;
        }
        return col.render(_, row);
      },
    };
  });

  const fromOptions = MONTHS.filter(m => m <= toMonth);
  const toOptions   = MONTHS.filter(m => m >= fromMonth);

  return (
    <div style={{ padding: 24 }}>
      <Title level={4} style={{ marginTop: 0, marginBottom: 16 }}>Team Max CCaaS Stats</Title>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
        <Select
          mode="multiple"
          value={selectedTeamIds}
          onChange={setSelectedTeamIds}
          style={{ minWidth: 260 }}
          placeholder="Select at least 1 team..."
          optionFilterProp="children"
          maxTagCount="responsive"
        >
          {teams.map(t => <Option key={t.ID} value={t.ID}>{t.Name}</Option>)}
        </Select>

        <Select value={fromMonth} onChange={v => { setFromMonth(v); if (v > toMonth) setToMonth(v); }} style={{ width: 130 }}>
          {fromOptions.map(m => <Option key={m} value={m}>{formatMonthKey(m)}</Option>)}
        </Select>
        <span style={{ fontSize: 13, color: 'var(--ant-color-text-secondary)' }}>to</span>
        <Select value={toMonth} onChange={v => { setToMonth(v); if (v < fromMonth) setFromMonth(v); }} style={{ width: 130 }}>
          {toOptions.map(m => <Option key={m} value={m}>{formatMonthKey(m)}</Option>)}
        </Select>

        <Button
          type="primary"
          icon={<SearchOutlined />}
          onClick={handleSearch}
          disabled={!selectedTeamIds.length}
        >
          Search
        </Button>
      </div>

      <Spin spinning={loading}>
        {!searched ? (
          <div style={{ color: 'var(--ant-color-text-tertiary)', padding: '24px 0' }}>
            Select at least one team and click Search.
          </div>
        ) : (
          <>
            {/* ── Team summary table (aggregated across months) ── */}
            <Table
              rowKey="TeamID"
              dataSource={tableRows}
              columns={tableColumns}
              size="small"
              pagination={false}
              sticky
              scroll={{ x: 'max-content' }}
            />

            {/* ── Month-over-month pivot ── */}
            {pivotMonths.length > 0 && (
              <>
                <Divider style={{ margin: '24px 0 16px' }} />
                <div className="sticky-table-label" style={{ marginBottom: 12 }}>
                  <Title level={5} style={{ margin: 0 }}>Month-over-Month</Title>
                </div>
                <Table
                  rowKey="_key"
                  dataSource={pivotRowsWithCalls}
                  columns={pivotColumnsForCalls}
                  size="small"
                  pagination={false}
                  sticky={{ offsetHeader: STICKY_OFFSET }}
                  scroll={{ x: 'max-content' }}
                  rowClassName={row => row._key === '__calls__' ? 'pivot-calls-row' : ''}
                />
              </>
            )}

            {/* ── Hour-over-Hour pivot ── */}
            {hourPivotRows.length > 0 && (
              <>
                <Divider style={{ margin: '24px 0 16px' }} />
                <div className="sticky-table-label" style={{ marginBottom: 12 }}>
                  <Title level={5} style={{ margin: 0 }}>Hour over Hour</Title>
                </div>
                <Table
                  rowKey="_key"
                  dataSource={hourPivotRowsWithCalls}
                  columns={hourPivotColumns}
                  size="small"
                  pagination={false}
                  sticky={{ offsetHeader: STICKY_OFFSET }}
                  scroll={{ x: 'max-content' }}
                  rowClassName={row => row._key === '__calls__' ? 'pivot-calls-row' : ''}
                />
              </>
            )}
          </>
        )}
      </Spin>
    </div>
  );
}
